import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail,
  Clock,
  Loader2,
  Plus,
  Edit2,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  FileText,
  Users,
  Building2,
  DollarSign,
  BarChart3,
  Wallet,
  Play,
  Settings as SettingsIcon,
  CalendarClock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'financial', label: 'Informe Financiero Mensual', icon: BarChart3, color: 'bg-blue-500' },
  { value: 'commissions', label: 'Reporte de Comisiones', icon: DollarSign, color: 'bg-orange-500' },
  { value: 'studies', label: 'Estudios Realizados', icon: FileText, color: 'bg-purple-500' },
  { value: 'expenses', label: 'Reporte de Gastos e Ingresos', icon: Wallet, color: 'bg-red-500' },
  { value: 'society', label: 'Informe de Sociedad', icon: Building2, color: 'bg-emerald-500' },
];

const ScheduledReports = () => {
  const { token, hasRole } = useAuth();
  const [reports, setReports] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [deletingReport, setDeletingReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  // Bulk schedule config state
  const [bulkConfig, setBulkConfig] = useState(null);
  const [bulkConfigDialog, setBulkConfigDialog] = useState(false);
  const [savingBulkConfig, setSavingBulkConfig] = useState(false);
  const [bulkConfigForm, setBulkConfigForm] = useState({
    recipients: '',
    day_of_month: '1',
    is_active: true
  });

  const initialForm = {
    name: '',
    report_type: '',
    society_id: '',
    society_name: '',
    recipients: '',
    day_of_month: '1',
    is_active: true
  };
  const [form, setForm] = useState(initialForm);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (hasRole(['SUPERADMIN'])) {
      fetchReports();
      fetchSocieties();
      fetchBulkConfig();
    }
  }, []);

  const fetchBulkConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduled-reports/bulk-config`, { headers });
      setBulkConfig(response.data);
      if (response.data.configured) {
        setBulkConfigForm({
          recipients: response.data.recipients?.join(', ') || '',
          day_of_month: response.data.day_of_month?.toString() || '1',
          is_active: response.data.is_active ?? true
        });
      }
    } catch (error) {
      console.error('Error fetching bulk config:', error);
    }
  };

  const handleSaveBulkConfig = async () => {
    const emails = bulkConfigForm.recipients.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    if (emails.length === 0) {
      toast.error('Ingrese al menos un email válido');
      return;
    }

    setSavingBulkConfig(true);
    try {
      await axios.post(`${API_URL}/api/scheduled-reports/bulk-config`, {
        recipients: emails,
        day_of_month: parseInt(bulkConfigForm.day_of_month),
        is_active: bulkConfigForm.is_active
      }, { headers });

      toast.success('Configuración de envío automático guardada');
      setBulkConfigDialog(false);
      fetchBulkConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingBulkConfig(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduled-reports`, { headers });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
      toast.error('Error al cargar los reportes programados');
    } finally {
      setLoading(false);
    }
  };

  const fetchSocieties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/societies?active_only=true`, { headers });
      setSocieties(response.data);
    } catch (error) {
      console.error('Error fetching societies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.report_type || !form.recipients) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    // Validate emails
    const emails = form.recipients.split(',').map(e => e.trim()).filter(e => e);
    const invalidEmails = emails.filter(e => !e.includes('@'));
    if (invalidEmails.length > 0) {
      toast.error(`Emails inválidos: ${invalidEmails.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        report_type: form.report_type,
        recipients: emails,
        day_of_month: parseInt(form.day_of_month),
        is_active: form.is_active
      };

      if (form.report_type === 'society') {
        if (!form.society_id) {
          toast.error('Seleccione una sociedad para este tipo de reporte');
          setSaving(false);
          return;
        }
        payload.society_id = form.society_id;
        payload.society_name = form.society_name;
      }

      if (editingReport) {
        await axios.put(`${API_URL}/api/scheduled-reports/${editingReport.id}`, payload, { headers });
        toast.success('Reporte actualizado exitosamente');
      } else {
        await axios.post(`${API_URL}/api/scheduled-reports`, payload, { headers });
        toast.success('Reporte programado creado exitosamente');
      }

      setIsModalOpen(false);
      setForm(initialForm);
      setEditingReport(null);
      fetchReports();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el reporte');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setForm({
      name: report.name,
      report_type: report.report_type,
      society_id: report.society_id || '',
      society_name: report.society_name || '',
      recipients: report.recipients.join(', '),
      day_of_month: report.day_of_month?.toString() || '1',
      is_active: report.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingReport) return;
    
    try {
      await axios.delete(`${API_URL}/api/scheduled-reports/${deletingReport.id}`, { headers });
      toast.success('Reporte eliminado');
      setDeletingReport(null);
      fetchReports();
    } catch (error) {
      toast.error('Error al eliminar el reporte');
    }
  };

  const handleSendNow = async (reportId) => {
    setSendingReport(reportId);
    try {
      const response = await axios.post(`${API_URL}/api/scheduled-reports/${reportId}/send-now`, {}, { headers });
      
      if (response.data.success) {
        toast.success(`Reporte enviado a ${response.data.sent_to.length} destinatario(s)`);
      } else {
        toast.error('Error al enviar el reporte');
      }
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      
      fetchReports();
    } catch (error) {
      console.error('Error sending report:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el reporte');
    } finally {
      setSendingReport(null);
    }
  };

  const handleSendAll = async () => {
    setSendingAll(true);
    try {
      const response = await axios.post(`${API_URL}/api/scheduled-reports/send-all`, {}, { headers });
      toast.success(response.data.message);
      
      // Show individual results
      response.data.results?.forEach(r => {
        if (r.status === 'error') {
          toast.error(`${r.report}: ${r.error}`);
        }
      });
      
      fetchReports();
    } catch (error) {
      console.error('Error sending all reports:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar los reportes');
    } finally {
      setSendingAll(false);
    }
  };

  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkEmailDialog, setBulkEmailDialog] = useState(false);
  const [bulkRecipients, setBulkRecipients] = useState('');

  const handleSendBulk = async () => {
    const emails = bulkRecipients.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    if (emails.length === 0) {
      toast.error('Ingrese al menos un email válido');
      return;
    }

    setSendingBulk(true);
    try {
      const response = await axios.post(`${API_URL}/api/scheduled-reports/send-bulk`, {
        recipients: emails,
        report_ids: [] // Empty = all active reports
      }, { headers });

      if (response.data.success) {
        toast.success(`Email enviado con ${response.data.attachment_count} PDFs adjuntos a ${response.data.sent_to.length} destinatario(s)`);
        setBulkEmailDialog(false);
        setBulkRecipients('');
      } else {
        toast.error('Error al enviar');
      }

      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      console.error('Error sending bulk:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar los reportes');
    } finally {
      setSendingBulk(false);
    }
  };

  const getReportTypeInfo = (type) => {
    return REPORT_TYPES.find(r => r.value === type) || REPORT_TYPES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!hasRole(['SUPERADMIN'])) {
    return null;
  }

  return (
    <Card className="border-blue-200" data-testid="scheduled-reports-section">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Informes Programados por Email</CardTitle>
              <CardDescription>Configure qué informes enviar mensualmente y a quién</CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setBulkEmailDialog(true)}
              disabled={reports.filter(r => r.is_active).length === 0}
              variant="outline"
              className="border-purple-500 text-purple-600 hover:bg-purple-50"
            >
              <FileText className="w-4 h-4 mr-2" />
              Enviar Todo en 1 Email
            </Button>
            <Button
              onClick={handleSendAll}
              disabled={sendingAll || reports.filter(r => r.is_active).length === 0}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            >
              {sendingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Enviar Separados
            </Button>
            <Button
              onClick={() => {
                setEditingReport(null);
                setForm(initialForm);
                setIsModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Reporte
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay informes programados configurados</p>
            <p className="text-sm">Crea uno para recibir reportes automáticos por email</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => {
              const typeInfo = getReportTypeInfo(report.report_type);
              const Icon = typeInfo.icon;
              
              return (
                <div
                  key={report.id}
                  className={`p-4 rounded-lg border ${report.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 ${typeInfo.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-slate-900">{report.name}</h4>
                          <Badge variant={report.is_active ? 'default' : 'secondary'}>
                            {report.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {typeInfo.label}
                          {report.society_name && ` - ${report.society_name}`}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {report.recipients.length} destinatario(s)
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Día {report.day_of_month} de cada mes
                          </span>
                        </div>
                        {report.last_sent_at && (
                          <p className="text-xs text-slate-400 mt-1">
                            Último envío: {formatDate(report.last_sent_at)}
                            {report.last_sent_status && ` - ${report.last_sent_status}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendNow(report.id)}
                        disabled={sendingReport === report.id}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        {sendingReport === report.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(report)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => setDeletingReport(report)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Recipients preview */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500">
                      <span className="font-medium">Destinatarios:</span>{' '}
                      {report.recipients.join(', ')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Auto Schedule Config Card */}
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <CalendarClock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-purple-900">Envío Automático Mensual</h4>
                <p className="text-sm text-purple-700 mt-1">
                  {bulkConfig?.configured && bulkConfig?.is_active ? (
                    <>
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <CheckCircle2 className="w-4 h-4" /> Activo
                      </span>
                      {' - '}Día {bulkConfig.day_of_month} de cada mes a {bulkConfig.recipients?.length || 0} destinatario(s)
                    </>
                  ) : (
                    'Configure el envío automático de todos los reportes en un solo email'
                  )}
                </p>
                {bulkConfig?.last_sent_at && (
                  <p className="text-xs text-purple-500 mt-1">
                    Último envío: {formatDate(bulkConfig.last_sent_at)} - {bulkConfig.last_sent_status}
                  </p>
                )}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setBulkConfigDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <SettingsIcon className="w-4 h-4 mr-1" />
              Configurar
            </Button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">ℹ️ Información</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Los reportes se generan con datos del <strong>mes anterior</strong> completo</li>
            <li>• Los PDFs se adjuntan automáticamente al email</li>
            <li>• Use "Enviar Ahora" para probar o enviar manualmente</li>
            <li>• Configure un reporte por cada sociedad si desea informes individuales</li>
          </ul>
        </div>
      </CardContent>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingReport ? 'Editar Reporte Programado' : 'Nuevo Reporte Programado'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre del Reporte *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Informe Financiero Mensual"
                required
              />
            </div>

            <div>
              <Label>Tipo de Reporte *</Label>
              <Select
                value={form.report_type}
                onValueChange={(value) => setForm({ ...form, report_type: value, society_id: '', society_name: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.report_type === 'society' && (
              <div>
                <Label>Sociedad *</Label>
                <Select
                  value={form.society_id}
                  onValueChange={(value) => {
                    const society = societies.find(s => s.id === value);
                    setForm({ 
                      ...form, 
                      society_id: value,
                      society_name: society?.name || ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar sociedad" />
                  </SelectTrigger>
                  <SelectContent>
                    {societies.map((society) => (
                      <SelectItem key={society.id} value={society.id}>
                        {society.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Destinatarios (emails) *</Label>
              <Input
                value={form.recipients}
                onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                placeholder="email1@ejemplo.com, email2@ejemplo.com"
              />
              <p className="text-xs text-slate-500 mt-1">Separe múltiples emails con coma</p>
            </div>

            <div>
              <Label>Día de envío (del mes)</Label>
              <Select
                value={form.day_of_month}
                onValueChange={(value) => setForm({ ...form, day_of_month: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(28)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      Día {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="is_active" className="cursor-pointer">Activo</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingReport ? 'Guardar Cambios' : 'Crear Reporte'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingReport} onOpenChange={() => setDeletingReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar reporte programado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la configuración de "{deletingReport?.name}".
              Los emails ya enviados no se verán afectados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Email Dialog */}
      <Dialog open={bulkEmailDialog} onOpenChange={setBulkEmailDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Enviar Todos los Reportes en 1 Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700">
                Se generarán <strong>{reports.filter(r => r.is_active).length} PDFs</strong> y se adjuntarán 
                todos en un solo email.
              </p>
            </div>
            <div>
              <Label>Destinatarios (emails) *</Label>
              <Input
                value={bulkRecipients}
                onChange={(e) => setBulkRecipients(e.target.value)}
                placeholder="email1@ejemplo.com, email2@ejemplo.com"
              />
              <p className="text-xs text-slate-500 mt-1">Separe múltiples emails con coma</p>
            </div>
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-2">Reportes que se incluirán:</p>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {reports.filter(r => r.is_active).map(r => (
                  <li key={r.id} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    {r.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSendBulk} 
              disabled={sendingBulk || !bulkRecipients.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingBulk ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Email con {reports.filter(r => r.is_active).length} PDFs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Auto Schedule Config Dialog */}
      <Dialog open={bulkConfigDialog} onOpenChange={setBulkConfigDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-purple-600" />
              Configurar Envío Automático Mensual
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700">
                El sistema enviará automáticamente <strong>todos los reportes activos</strong> en un solo email 
                el día configurado de cada mes.
              </p>
            </div>

            <div>
              <Label>Destinatarios (emails) *</Label>
              <Input
                value={bulkConfigForm.recipients}
                onChange={(e) => setBulkConfigForm({ ...bulkConfigForm, recipients: e.target.value })}
                placeholder="email1@ejemplo.com, email2@ejemplo.com"
              />
              <p className="text-xs text-slate-500 mt-1">Separe múltiples emails con coma</p>
            </div>

            <div>
              <Label>Día de envío (del mes)</Label>
              <Select
                value={bulkConfigForm.day_of_month}
                onValueChange={(value) => setBulkConfigForm({ ...bulkConfigForm, day_of_month: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(28)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      Día {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="bulk_is_active"
                checked={bulkConfigForm.is_active}
                onChange={(e) => setBulkConfigForm({ ...bulkConfigForm, is_active: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="bulk_is_active" className="cursor-pointer">Activar envío automático</Label>
            </div>

            <div className="text-sm text-slate-600 border-t pt-3">
              <p className="font-medium mb-2">Reportes que se incluirán ({reports.filter(r => r.is_active).length}):</p>
              <ul className="space-y-1 max-h-24 overflow-y-auto">
                {reports.filter(r => r.is_active).map(r => (
                  <li key={r.id} className="flex items-center gap-2 text-xs">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    {r.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfigDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveBulkConfig} 
              disabled={savingBulkConfig || !bulkConfigForm.recipients.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingBulkConfig ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Guardar Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ScheduledReports;
